import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { IoArrowUndoOutline } from 'react-icons/io5';

import {
  SAFETY,
  Section,
  badge,
  errorText,
  formatDate,
  input,
  mono,
  primary,
  secondary,
} from './PipelineSettingsPanel';
import { confirmDialog } from '../../components/ConfirmDialog';
import { configApi } from '../../services/api';

// Application-wide numeric settings of one area (Configuration → Settings,
// and the meetings pipeline's Numbers tab). Each row says what it moves, how
// safe it is to move, and whether running code reads it yet. A publish is a
// new version with a reason; "Reset" puts one key back to its code default.

const fail = (error) => toast.error(errorText(error));

const parseValue = (item, raw) => {
  if (raw === null) return null;
  if (item.kind === 'BOOL') return raw;
  if (item.kind === 'INT') return Number.parseInt(raw, 10);
  if (item.kind === 'FLOAT') return Number.parseFloat(raw);
  return raw;
};

const shown = (value) => (value === null ? 'default' : String(value));

export default function SettingsEditor({
  token,
  area,
  canManage,
  groupLabel = (stage) => stage,
  onPublished,
}) {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [changes, setChanges] = useState({});
  const [rationale, setRationale] = useState('');
  // datetime-local value; empty means "now".
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [current, past] = await Promise.all([
      configApi.settings(token, area),
      configApi.history(token),
    ]);
    setConfig(current);
    setHistory(past.versions || []);
    setChanges({});
  }, [token, area]);

  useEffect(() => {
    load().catch(fail);
  }, [load]);

  const items = useMemo(() => config?.items || [], [config]);
  const groups = useMemo(() => {
    const byStage = {};
    items.forEach((item) => {
      (byStage[item.stage] = byStage[item.stage] || []).push(item);
    });
    return Object.entries(byStage);
  }, [items]);

  if (!config) return <p className="text-sm text-zinc-400">Loading…</p>;

  const keys = new Set(items.map((i) => i.key));
  const mine = history.filter((h) =>
    Object.keys(h.changes || {}).some((k) => keys.has(k)),
  );
  const effective = config.effective || {};
  const overridden = new Set(config.overridden_keys || []);
  const changedKeys = Object.keys(changes);
  const sensitive = items.filter(
    (item) => item.safety === 'SENSITIVE' && changedKeys.includes(item.key),
  );
  const set = (key, value) => setChanges({ ...changes, [key]: value });
  const needsApproval = config.require_second_approval && sensitive.length > 0;
  const action = needsApproval
    ? 'Request approval'
    : when
      ? 'Schedule'
      : 'Publish new version';

  const publish = async () => {
    const timing = when
      ? `Takes effect ${new Date(when).toLocaleString()}.`
      : 'Every process picks this up on its next read, within seconds.';
    const ok = await confirmDialog({
      title: `${action}: ${changedKeys.length} change(s)?`,
      message: [
        sensitive.length
          ? `Sensitive: ${sensitive.map((s) => s.key).join(', ')}. Records made after this are not directly comparable with older ones.`
          : '',
        needsApproval ? 'Another admin must approve it first.' : '',
        timing,
      ]
        .filter(Boolean)
        .join(' '),
      confirmLabel: action,
    });
    if (!ok) return;
    setBusy(true);
    try {
      const values = Object.fromEntries(
        items
          .filter((item) => changedKeys.includes(item.key))
          .map((item) => [item.key, parseValue(item, changes[item.key])]),
      );
      const result = await configApi.publish(
        token,
        values,
        rationale,
        when ? new Date(when).toISOString() : null,
      );
      setRationale('');
      setWhen('');
      await load();
      onPublished?.();
      toast.success(
        result?.request_id
          ? 'Sent for approval'
          : when
            ? 'Scheduled'
            : 'Published',
      );
    } catch (error) {
      fail(error);
    } finally {
      setBusy(false);
    }
  };

  const cancelScheduled = async () => {
    const ok = await confirmDialog({
      title: `Cancel scheduled v${config.scheduled.version}?`,
      message: 'Nothing has read it yet; the current version stays.',
      confirmLabel: 'Cancel it',
      danger: true,
    });
    if (!ok) return;
    try {
      await configApi.cancelScheduled(token);
      await load();
      toast.success('Scheduled version cancelled');
    } catch (error) {
      fail(error);
    }
  };

  const scheduledHere = Object.keys(config.scheduled?.changes || {}).filter(
    (k) => keys.has(k),
  );

  return (
    <div className="space-y-4">
      {config.scheduled && (
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-sky-900/60 bg-sky-950/30 px-3 py-2 text-sm text-sky-200">
          <div>
            v{config.scheduled.version} takes effect{' '}
            {formatDate(config.scheduled.effective_from)}:{' '}
            {Object.entries(config.scheduled.changes)
              .map(([k, v]) => `${k}=${shown(v)}`)
              .join(', ')}
            {scheduledHere.length === 0 && ' (other areas)'}. No other change
            can be published until then.
          </div>
          {canManage && (
            <button className={secondary} onClick={cancelScheduled}>
              Cancel it
            </button>
          )}
        </div>
      )}
      {groups.map(([stage, rows]) => (
        <Section key={stage} title={groupLabel(stage)}>
          <div className="divide-y divide-zinc-800">
            {rows.map((item) => {
              const pending = item.key in changes;
              const value = pending
                ? (changes[item.key] ?? item.default)
                : effective[item.key];
              const locked = !canManage || item.safety === 'BREAKING';
              return (
                <div
                  key={item.key}
                  className="grid gap-3 py-3 md:grid-cols-[minmax(0,1fr)_220px]"
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`${mono} text-zinc-200`}>
                        {item.key}
                      </span>
                      <span className={`${badge} ${SAFETY[item.safety].tone}`}>
                        {item.safety}
                      </span>
                      {overridden.has(item.key) && !pending && (
                        <span
                          className={`${badge} bg-indigo-500/10 text-indigo-300`}
                        >
                          changed
                        </span>
                      )}
                      {pending && (
                        <span className={`${badge} bg-sky-500/10 text-sky-300`}>
                          unsaved
                        </span>
                      )}
                      {!item.live && item.safety !== 'BREAKING' && (
                        <span
                          className={`${badge} bg-zinc-800 text-zinc-400`}
                          title="Stored and versioned, but no running code reads it yet."
                        >
                          not read yet
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-zinc-400">{item.effect}</p>
                    {item.caution && (
                      <p className="mt-1 text-xs text-amber-400">
                        {item.caution}
                      </p>
                    )}
                    <p className="mt-1 text-xs tabular-nums text-zinc-400">
                      Default {String(item.default)}
                      {item.unit ? ` ${item.unit}` : ''}
                      {item.minimum != null || item.maximum != null
                        ? ` · range ${item.minimum ?? '…'} to ${item.maximum ?? '…'}`
                        : ''}
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    {item.kind === 'BOOL' ? (
                      <input
                        type="checkbox"
                        aria-label={item.key}
                        checked={Boolean(value)}
                        disabled={locked}
                        onChange={(e) => set(item.key, e.target.checked)}
                      />
                    ) : (
                      <input
                        aria-label={item.key}
                        className={`${input} tabular-nums`}
                        type={item.kind === 'STRING' ? 'text' : 'number'}
                        step={item.kind === 'FLOAT' ? 'any' : 1}
                        min={item.minimum ?? undefined}
                        max={item.maximum ?? undefined}
                        value={value ?? ''}
                        disabled={locked}
                        onChange={(e) => set(item.key, e.target.value)}
                      />
                    )}
                    {!locked && overridden.has(item.key) && !pending && (
                      <button
                        className={secondary}
                        title="Back to the code default"
                        onClick={() => set(item.key, null)}
                      >
                        <IoArrowUndoOutline aria-hidden /> Reset
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Section>
      ))}

      {canManage && changedKeys.length > 0 && (
        <div className="sticky bottom-0 space-y-2 rounded-lg border border-indigo-800 bg-zinc-950 p-4">
          <p className="text-sm text-zinc-300">
            {changedKeys.length} unsaved change(s):{' '}
            {changedKeys.map((k) => `${k} → ${shown(changes[k])}`).join(', ')}
          </p>
          <input
            className={input}
            placeholder="Why are you changing this? (required, kept in history)"
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
          <label className="flex flex-wrap items-center gap-2 text-sm text-zinc-300">
            Take effect
            <input
              type="datetime-local"
              className={`${input} w-auto`}
              value={when}
              onChange={(e) => setWhen(e.target.value)}
            />
            <span className="text-xs text-zinc-400">
              {when ? 'scheduled' : 'empty = now'}
            </span>
          </label>
          {needsApproval && (
            <p className="text-xs text-amber-300">
              Includes sensitive settings: another admin approves it under
              Approvals before it is published.
            </p>
          )}
          <div className="flex gap-2">
            <button
              className={primary}
              disabled={busy || !rationale.trim()}
              onClick={publish}
            >
              {action}
            </button>
            <button className={secondary} onClick={() => setChanges({})}>
              Undo changes
            </button>
          </div>
        </div>
      )}

      <Section title="History" hint="Versions that changed this area.">
        {mine.length === 0 ? (
          <p className="text-sm text-zinc-400">
            No changes yet: everything runs on the code defaults.
          </p>
        ) : (
          <ul className="divide-y divide-zinc-800">
            {mine.map((h) => (
              <li key={h.version} className="py-2 text-sm">
                <span className={`${mono} text-zinc-200`}>v{h.version}</span>{' '}
                <span className="text-zinc-400">
                  {formatDate(h.created_at)} · {h.changed_by}
                </span>
                <div className="text-zinc-300">{h.rationale}</div>
                <div className={`${mono} text-zinc-400`}>
                  {Object.entries(h.changes || {})
                    .filter(([k]) => keys.has(k))
                    .map(([k, v]) => `${k}=${shown(v)}`)
                    .join(', ')}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
